import React, { useState, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import MobileBottomNav from "./components/MobileBottomNav";
import { Badge } from "./components/ui/badge";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { 
  RefreshCw, 
  Plus, 
  LogOut, 
  CheckCircle, 
  XCircle, 
  Clock,
  CreditCard,
  User,
  Users,
  Building2,
  Mail,
  Bell,
  Settings,
  BarChart3,
  FileText,
  Edit,
  Trash2,
  Send,
  AlertTriangle,
  Home,
  DollarSign,
  Calendar,
  Eye,
  EyeOff,
  X,
  MessageCircle,
  BookOpen
} from "lucide-react";
import { agentAdvancerSenzaSfondo } from './images';
import { toast } from "sonner";
import CompatibiliCandidati from "./components/CompatibiliCandidati";
import AdminCandidatiList from "./components/AdminCandidatiList";
import AdminAgenzieList from "./components/AdminAgenzieList";
import AdminHeaderDropdown from "./components/AdminHeaderDropdown";
import AdminNotifications from "./components/AdminNotificationsSimple";
import AdminPendingAgencies from "./components/AdminPendingAgencies";
import MatchAdmin from "./components/MatchAdmin";
import AdminSupporto from "./components/AdminSupporto";
import AdminFormazione from "./components/AdminFormazione";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  // Blocca accesso se non admin: usa solo admin_token
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      window.location.href = '/login';
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.user_type !== 'admin') {
        // Se non è admin, cancella il token e reindirizza
        localStorage.removeItem('admin_token');
        window.location.href = '/login';
      }
    } catch (e) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
  }, []);
  const navigate = useNavigate();
  const [agenzie, setAgenzie] = useState([]);
  const [candidati, setCandidati] = useState([]);
  const [pagamenti, setPagamenti] = useState([]);
  const [noteInterne, setNoteInterne] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showNuovoPagamentoModal, setShowNuovoPagamentoModal] = useState(false);
  const [showNuovaNotaModal, setShowNuovaNotaModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingPagamento, setEditingPagamento] = useState(null);
  const [editingNota, setEditingNota] = useState(null);
  const [agenzieReady, setAgenzieReady] = useState(false);
  const [selectedAgenzia, setSelectedAgenzia] = useState(null);
  
  // Impersonation states
  const [adminUsersList, setAdminUsersList] = useState({ candidati: [], agenzie: [] });
  const [impersonationSession, setImpersonationSession] = useState(null);
  const [loadingImpersonation, setLoadingImpersonation] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Modal states
  const [nuovoPagamento, setNuovoPagamento] = useState({
    agenzia_id: "",
    piano: "START",
    modalita_pagamento: "unico",
    data_attivazione: "",
    stato: "in_attesa",
    note: ""
  });

  const [nuovaNota, setNuovaNota] = useState({
    tipo_entita: "candidato",
    entita_id: "",
    testo: "",
    esito: "positivo"
  });

  // Auto-login admin on mount
  useEffect(() => {
    const autoLoginAdmin = async () => {
      // Check if already logged in
      const token = localStorage.getItem('admin_token');
      if (token) {
        console.log("Admin already logged in");
        loadData();
        loadNotificationsCount();
        return;
      }

      // Auto-login with default credentials
      try {
        const response = await fetch(`${API}/api/v1/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: 'admin@inleads.com',
            password: 'admin123'
          })
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('admin_token', data.access_token);
          console.log("Admin auto-login successful");
          loadData();
          loadNotificationsCount();
        } else {
          console.error("Admin auto-login failed");
          toast.error("Errore autenticazione admin");
        }
      } catch (error) {
        console.error("Admin login error:", error);
      }
    };

    autoLoginAdmin();
  }, []);

  useEffect(() => {
    // Removed: loadData() and loadNotificationsCount() - now called after auto-login
    
    // Read tab from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const tabFromUrl = urlParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
    
    // Listen for tab changes from mobile navigation
    const handleTabChange = (event) => {
      if (event.detail) {
        setActiveTab(event.detail);
      }
    };
    
    // Catch ResizeObserver errors
    const handleResizeObserverError = (e) => {
      if (e.message === 'ResizeObserver loop limit exceeded' || 
          e.message.includes('ResizeObserver loop completed')) {
        e.preventDefault();
        e.stopPropagation();
        console.warn('ResizeObserver error caught and suppressed');
      }
    };
    
    window.addEventListener('tabChange', handleTabChange);
    window.addEventListener('error', handleResizeObserverError);
    window.addEventListener('unhandledrejection', handleResizeObserverError);
    
    return () => {
      window.removeEventListener('tabChange', handleTabChange);
      window.removeEventListener('error', handleResizeObserverError);
      window.removeEventListener('unhandledrejection', handleResizeObserverError);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log("Loading admin data...");
      
      const token = localStorage.getItem('admin_token');
      if (!token) {
        console.log("No admin token found");
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Load data with better error handling
      let agenzieData = [];
      let candidatiData = [];
      let pagamentiData = [];
      let usersListData = { agenzie: [], candidati: [] };

      try {
        const agenzieRes = await fetch(`${API}/v1/admin/agenzie`, { headers });
        if (agenzieRes.ok) {
          agenzieData = await agenzieRes.json();
          console.log("Loaded agenzie:", agenzieData.length, agenzieData);
        }
      } catch (e) {
        console.error("Error loading agenzie:", e);
      }

      try {
        const candidatiRes = await fetch(`${API}/v1/admin/candidati`, { headers });
        if (candidatiRes.ok) {
          candidatiData = await candidatiRes.json();
          console.log("Loaded candidati:", candidatiData.length, candidatiData);
        }
      } catch (e) {
        console.error("Error loading candidati:", e);
      }

      try {
        const pagamentiRes = await fetch(`${API}/v1/admin/pagamenti`, { headers });
        if (pagamentiRes.ok) {
          pagamentiData = await pagamentiRes.json();
          console.log("Loaded pagamenti:", pagamentiData.length, pagamentiData);
        }
      } catch (e) {
        console.error("Error loading pagamenti:", e);
      }

      // Use the same data for users list (impersonation)
      usersListData = {
        agenzie: agenzieData,
        candidati: candidatiData
      };
        
      setAgenzie(agenzieData);
      setCandidati(candidatiData);
      setPagamenti(pagamentiData);
      setAdminUsersList(usersListData);
      
      // Generate mock data only for internal notes
      setNoteInterne(generateMockNote(agenzieData));
      
      // Delay setting agencies ready to avoid ResizeObserver issues
      setTimeout(() => {
        setAgenzieReady(true);
      }, 500);
      
      console.log("Admin data loading completed");
    } catch (error) {
      console.error("Errore nel caricamento dati:", error);
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  // Load notifications count
  const loadNotificationsCount = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;

      const response = await fetch(`${API}/v1/admin/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadNotifications(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error loading notifications count:", error);
    }
  };

  // Generate mock payments data
  const generateMockPagamenti = (agenzieData) => {
    const stati = ["Pagato", "In attesa", "Scaduto"];
    const tipi = ["Abbonamento", "Setup", "Extra"];
    
    return agenzieData.slice(0, 20).map((agenzia, index) => ({
      id: `pag_${index + 1}`,
      agenzia_id: agenzia.id,
      agenzia_nome: agenzia.nome_agenzia,
      importo: Math.floor(Math.random() * 500) + 100,
      tipo: tipi[Math.floor(Math.random() * tipi.length)],
      stato: stati[Math.floor(Math.random() * stati.length)],
      scadenza: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      data_creazione: new Date(Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000).toISOString(),
      note: "Abbonamento mensile App"
    }));
  };

  // Generate mock internal notes
  const generateMockNote = (agenzieData) => {
    const priorita = ["alta", "normale", "bassa"];
    const titoli = [
      "Richiesta supporto tecnico",
      "Aggiornamento contratto",
      "Follow-up commerciale",
      "Verifica documentazione",
      "Rinnovo abbonamento"
    ];

    return agenzieData.slice(0, 15).map((agenzia, index) => ({
      id: `nota_${index + 1}`,
      tipo_entita: "agenzia",
      entita_id: agenzia.id,
      entita_nome: agenzia.nome_agenzia,
      titolo: titoli[Math.floor(Math.random() * titoli.length)],
      contenuto: `Nota di esempio per ${agenzia.nome_agenzia}. Questa è una nota interna generata automaticamente per demo.`,
      priorita: priorita[Math.floor(Math.random() * priorita.length)],
      autore: "Admin",
      data_creazione: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
      data_aggiornamento: new Date().toISOString()
    }));
  };

  // ================================================
  // ADMIN IMPERSONATION FUNCTIONS
  // ================================================
  
  const handleImpersonateUser = async (userType, userId, userName) => {
    try {
      setLoadingImpersonation(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API}/v1/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_type: userType,
          user_id: userId,
          admin_id: 'admin'
        })
      });
      
      if (response.ok) {
        const sessionData = await response.json();
        console.log('Risposta impersonificazione:', sessionData);
        setImpersonationSession(sessionData);

        toast.success(`Impersonazione avviata per ${userName}`);

        // Salva sempre il token impersonificato
        let token = sessionData.impersonated_token;
        if (!token && sessionData.session && sessionData.session.impersonated_token) {
          token = sessionData.session.impersonated_token;
        }
        if (token) {
          localStorage.setItem('impersonated_token', token);
          console.log('Token impersonificato salvato in localStorage:', token);
          // Delay per garantire che il token sia disponibile
          setTimeout(() => {
            if (userType === 'candidato') {
              window.location.href = `/#/candidato/${userId}?session=${sessionData.session.session_id}`;
            } else {
              window.location.href = `/#/agenzia/${userId}?session=${sessionData.session.session_id}`;
            }
          }, 300);
        } else {
          console.warn('Token impersonificato NON trovato nella risposta:', sessionData);
        }
      } else {
        const error = await response.json();
        toast.error(`Errore impersonazione: ${error.detail}`);
      }
    } catch (error) {
      console.error("Error starting impersonation:", error);
      toast.error("Errore nell'avvio impersonazione");
    } finally {
      setLoadingImpersonation(false);
    }
  };
  
  const handleEndImpersonation = async (sessionId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/v1/admin/impersonation/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setImpersonationSession(null);
        toast.success("Impersonazione terminata");
        window.location.href = '/#/admin-test'; // Torna al pannello admin con HashRouter
      } else {
        toast.error("Errore nella terminazione impersonazione");
      }
    } catch (error) {
      console.error("Error ending impersonation:", error);
      toast.error("Errore nella terminazione impersonazione");
    }
  };

  // Calculate statistics
  const getStats = () => {
    // Conteggio totale candidati e pagamenti
    const totaleCandidati = candidati.length;
    const totalePagamenti = pagamenti.length;
    // Log dettagliato per debug statistiche
    console.log('DEBUG candidati:', candidati);
    console.log('DEBUG pagamenti:', pagamenti);
  // Variabili di debug già dichiarate, non serve ridichiararle
    console.log('DEBUG stats:', {
      nuovo: candidati.filter(c => (c.stato_pipeline || '').toLowerCase() === 'nuovo').length,
      contattato: candidati.filter(c => (c.stato_pipeline || '').toLowerCase() === 'contattato').length,
      pagati: pagamenti.filter(p => (p.stato || '').toLowerCase() === 'pagato').length
    });
    const pagamentiStats = {
      pagati: pagamenti.filter(p => (p.stato || "").toLowerCase() === "pagato").length,
      in_attesa: pagamenti.filter(p => (p.stato || "").toLowerCase() === "in attesa").length,
      scaduti: pagamenti.filter(p => (p.stato || "").toLowerCase() === "scaduto").length,
      annullati: pagamenti.filter(p => (p.stato || "").toLowerCase() === "annullato").length,
      totale_incasso: pagamenti.filter(p => (p.stato || "").toLowerCase() === "pagato").reduce((sum, p) => sum + p.importo, 0)
    };

    const candidatiStats = {
      nuovo: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "nuovo").length,
      contattato: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "contattato").length,
      modulo_inviato: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "modulo inviato").length,
      form_compilato: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "form compilato").length,
      in_attesa_colloquio: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "in attesa colloquio").length,
      colloquio_fissato: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "colloquio fissato").length,
      inserito: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "inserito").length,
      scartato: candidati.filter(c => (c.stato_pipeline || "").toLowerCase() === "scartato").length
    };

    // Log dettagliato per debug statistiche
    console.log('DEBUG candidati:', candidati);
    console.log('DEBUG pagamenti:', pagamenti);
    // Log solo i conteggi
    console.log('DEBUG stats:', {
      nuovo: candidati.filter(c => (c.stato_pipeline || '').toLowerCase() === 'nuovo').length,
      contattato: candidati.filter(c => (c.stato_pipeline || '').toLowerCase() === 'contattato').length,
      pagati: pagamenti.filter(p => (p.stato || '').toLowerCase() === 'pagato').length
    });
    return { pagamentiStats, candidatiStats, totaleCandidati, totalePagamenti };
  };

  const { pagamentiStats, candidatiStats, totaleCandidati, totalePagamenti } = getStats();

  // Get status color for payments
  const getPagamentoStatusColor = (stato) => {
    const colors = {
      "pagato": "bg-green-100 text-green-800 border-green-300",
      "in_attesa": "bg-yellow-100 text-yellow-800 border-yellow-300",
      "scaduto": "bg-red-100 text-red-800 border-red-300",
      "annullato": "bg-gray-100 text-gray-800 border-gray-300"
    };
    return colors[stato] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  // Get priority color for notes
  const getNotaPriorityColor = (priorita) => {
    const colors = {
      "alta": "bg-red-100 text-red-800 border-red-300",
      "normale": "bg-blue-100 text-blue-800 border-blue-300", 
      "bassa": "bg-gray-100 text-gray-800 border-gray-300"
    };
    return colors[priorita] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  const closePagamentoModal = () => {
    setShowNuovoPagamentoModal(false);
    setEditingPagamento(null);
    setNuovoPagamento({
      agenzia_id: "",
      piano: "START",
      modalita_pagamento: "unico",
      data_attivazione: "",
      stato: "in_attesa",
      note: ""
    });
  };

  const handleNuovoPagamento = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      // Calculate plan details
      const piani = {
        START: { importo: 750, durata_mesi: 1 },
        GROW: { importo: 3500, durata_mesi: 6 },
        ELITE: { importo: 5000, durata_mesi: 10 }
      };
      
      const pianDetails = piani[nuovoPagamento.piano];
      
      // Calculate data scadenza
      const dataAttivazione = new Date(nuovoPagamento.data_attivazione);
      const dataScadenza = new Date(dataAttivazione);
      dataScadenza.setMonth(dataScadenza.getMonth() + pianDetails.durata_mesi);
      
      if (editingPagamento) {
        // Update existing payment - include stato
        const pagamentoPayload = {
          agenzia_id: parseInt(nuovoPagamento.agenzia_id),
          importo: pianDetails.importo,
          tipo: nuovoPagamento.piano.toLowerCase(),
          piano: nuovoPagamento.piano,
          modalita_pagamento: nuovoPagamento.modalita_pagamento,
          data_attivazione: nuovoPagamento.data_attivazione,
          data_scadenza: dataScadenza.toISOString().split('T')[0],
          stato: nuovoPagamento.stato,
          note: nuovoPagamento.note
        };
        
        const response = await fetch(`${API}/v1/admin/pagamenti/${editingPagamento}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(pagamentoPayload)
        });
        
        if (response.ok) {
          const updatedPagamento = await response.json();
          setPagamenti(prev => prev.map(p => p.id === editingPagamento ? updatedPagamento : p));
          toast.success("Pagamento aggiornato con successo!");
          closePagamentoModal();
        } else {
          const error = await response.json();
          toast.error(`Errore: ${error.detail || 'Impossibile aggiornare pagamento'}`);
        }
      } else {
        // Create new payment - don't send stato (backend sets it automatically)
        const pagamentoPayload = {
          agenzia_id: parseInt(nuovoPagamento.agenzia_id),
          importo: pianDetails.importo,
          tipo: nuovoPagamento.piano.toLowerCase(),
          piano: nuovoPagamento.piano,
          modalita_pagamento: nuovoPagamento.modalita_pagamento,
          data_attivazione: nuovoPagamento.data_attivazione,
          data_scadenza: dataScadenza.toISOString().split('T')[0],
          note: nuovoPagamento.note
        };
        
        const response = await fetch(`${API}/v1/admin/pagamenti`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(pagamentoPayload)
        });
        
        if (response.ok) {
          const newPagamento = await response.json();
          setPagamenti(prev => [newPagamento, ...prev]);
          toast.success("Pagamento creato con successo!");
          closePagamentoModal();
        } else {
          const error = await response.json();
          toast.error(`Errore: ${error.detail || 'Impossibile creare pagamento'}`);
        }
      }
    } catch (error) {
      console.error("Error with pagamento:", error);
      toast.error(`Errore nell'operazione sul pagamento`);
    }
  };

  const handleUpdatePagamento = async (pagamentoId, updates) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API}/v1/admin/pagamenti/${pagamentoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        const updatedPagamento = await response.json();
        setPagamenti(prev => prev.map(p => p.id === pagamentoId ? updatedPagamento : p));
        toast.success("Pagamento aggiornato con successo!");
      } else {
        toast.error("Errore nell'aggiornamento del pagamento");
      }
    } catch (error) {
      console.error("Error updating pagamento:", error);
      toast.error("Errore nell'aggiornamento del pagamento");
    }
  };

  const handleDeletePagamento = async (pagamentoId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo pagamento?")) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API}/v1/admin/pagamenti/${pagamentoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setPagamenti(prev => prev.filter(p => p.id !== pagamentoId));
        toast.success("Pagamento eliminato con successo!");
      } else {
        toast.error("Errore nell'eliminazione del pagamento");
      }
    } catch (error) {
      console.error("Error deleting pagamento:", error);
      toast.error("Errore nell'eliminazione del pagamento");
    }
  };

  const handleNuovaNota = async (e) => {
    e.preventDefault();
    try {
      const entitaNome = nuovaNota.tipo_entita === "agenzia" 
        ? agenzie.find(a => a.id === nuovaNota.entita_id)?.nome_agenzia 
        : candidati.find(c => c.id === nuovaNota.entita_id)?.nome + " " + candidati.find(c => c.id === nuovaNota.entita_id)?.cognome;

      if (editingNota) {
        // Update existing note
        const updatedNota = {
          ...nuovaNota,
          id: editingNota,
          entita_nome: entitaNome || "",
          autore: "Admin",
          data_aggiornamento: new Date().toISOString()
        };
        
        setNoteInterne(prev => prev.map(n => n.id === editingNota ? updatedNota : n));
        setEditingNota(null);
        toast.success("Nota aggiornata con successo");
      } else {
        // Create new note
        const newNota = {
          ...nuovaNota,
          id: `nota_${Date.now()}`,
          entita_nome: entitaNome || "",
          autore: "Admin",
          data_creazione: new Date().toISOString(),
          data_aggiornamento: new Date().toISOString()
        };
        
        setNoteInterne(prev => [newNota, ...prev]);
        toast.success("Nota aggiunta con successo");
      }
      
      setShowNuovaNotaModal(false);
      setNuovaNota({
        tipo_entita: "candidato",
        entita_id: "",
        testo: "",
        esito: "positivo"
      });
    } catch (error) {
      toast.error("Errore nell'aggiunta della nota");
    }
  };

  const sendEmailSollecito = async (pagamento) => {
    try {
      // Simulate API call
      toast.success(`Email di sollecito inviata a ${pagamento.agenzia_nome}`);
    } catch (error) {
      toast.error("Errore nell'invio dell'email");
    }
  };

  // Prevent background scroll
  useEffect(() => {
    if (showNuovoPagamentoModal || showNuovaNotaModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showNuovoPagamentoModal, showNuovaNotaModal]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-white to-blue-50/50 flex items-center justify-center" style={{fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif"}}>
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-12 shadow-xl border border-white/40">
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium text-center">Caricamento Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/30 via-white to-blue-50/50 pb-8 md:pb-8" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", sans-serif'}}>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-7xl overflow-hidden">
        
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-4 sm:p-6 shadow-xl border border-white/40">
            <div className="flex items-center justify-between gap-6">
              {/* Logo sempre a sinistra */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white rounded-3xl shadow-lg flex items-center justify-center"
                     style={{
                       boxShadow: '0 10px 40px -10px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
                       background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
                     }}>
                  <img 
                    src={agentAdvancerSenzaSfondo} 
                    alt="InLeads Logo" 
                    className="w-9 h-9 object-contain"
                  />
                </div>
                <div className="text-left">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">InLeads Admin</h1>
                  <p className="text-blue-600 font-medium text-sm">Pannello di Controllo</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Notification Bell */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab('notifiche')}
                  className="relative rounded-2xl border-gray-200 hover:bg-gray-50 p-2"
                >
                  <Bell className="w-5 h-5 text-gray-600" />
                  {unreadNotifications > 0 && (
                    <Badge className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                      {unreadNotifications > 99 ? '99+' : unreadNotifications}
                    </Badge>
                  )}
                </Button>
                
                <AdminHeaderDropdown 
                  onNavigate={navigate}
                  onTabChange={setActiveTab}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-4 sm:p-6 shadow-xl border border-white/40 overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            
            {/* Clean 7-Tab Navigation */}
            <TabsList className="grid w-full grid-cols-4 md:grid-cols-7 bg-gray-50 rounded-2xl p-1 sm:p-2 h-auto border border-gray-200 shadow-sm gap-1">
              <TabsTrigger 
                value="overview"
                className="rounded-xl font-semibold border border-gray-300 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:border-blue-500 transition-all duration-200 px-4 py-3 text-sm min-w-0 flex-shrink-0"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger 
                value="candidati"
                className="rounded-xl font-semibold border border-gray-300 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:border-blue-500 transition-all duration-200 px-4 py-3 text-sm min-w-0 flex-shrink-0"
              >
                <User className="w-4 h-4 mr-2" />
                Gestione Candidati
              </TabsTrigger>
              <TabsTrigger 
                value="agenzie"
                className="rounded-xl font-semibold border border-gray-300 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:border-blue-500 transition-all duration-200 px-4 py-3 text-sm min-w-0 flex-shrink-0"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Agenzie
              </TabsTrigger>
              <TabsTrigger 
                value="match"
                className="rounded-xl font-semibold border border-gray-300 hover:border-purple-300 hover:bg-purple-50 text-gray-700 hover:text-purple-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:border-purple-500 transition-all duration-200 px-4 py-3 text-sm min-w-0 flex-shrink-0"
              >
                <Users className="w-4 h-4 mr-2" />
                Match
              </TabsTrigger>
              <TabsTrigger 
                value="assistenza"
                className="rounded-xl font-semibold border border-gray-300 hover:border-green-300 hover:bg-green-50 text-gray-700 hover:text-green-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-green-600 data-[state=active]:text-white data-[state=active]:border-green-500 transition-all duration-200 px-4 py-3 text-sm min-w-0 flex-shrink-0"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Assistenza
              </TabsTrigger>
              <TabsTrigger 
                value="formazione"
                className="rounded-xl font-semibold border border-gray-300 hover:border-orange-300 hover:bg-orange-50 text-gray-700 hover:text-orange-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:border-orange-500 transition-all duration-200 px-4 py-3 text-sm min-w-0 flex-shrink-0"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Formazione
              </TabsTrigger>
              <TabsTrigger 
                value="impostazioni"
                className="rounded-xl font-semibold border border-gray-300 hover:border-blue-300 hover:bg-blue-50 text-gray-700 hover:text-blue-600 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:border-blue-500 transition-all duration-200 px-4 py-3 text-sm min-w-0 flex-shrink-0"
              >
                <Settings className="w-4 h-4 mr-2" />
                Impostazioni
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
              
              {/* Stats Cards - Original Layout */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Statistiche Candidati</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
                  <div className="bg-gray-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-gray-700 mb-2">{totaleCandidati}</div>
                    <div className="text-xs text-gray-600 font-medium leading-tight">Totale</div>
                  </div>
                  <div className="bg-blue-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-blue-700 mb-2">{candidatiStats.nuovo}</div>
                    <div className="text-xs text-blue-600 font-medium leading-tight">Nuovo</div>
                  </div>
                  
                  <div className="bg-blue-200/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-blue-300 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-blue-800 mb-2">{candidatiStats.contattato}</div>
                    <div className="text-xs text-blue-700 font-medium leading-tight">Contattato</div>
                  </div>
                  
                  <div className="bg-yellow-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-yellow-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-yellow-700 mb-2">{candidatiStats.modulo_inviato}</div>
                    <div className="text-xs text-yellow-600 font-medium leading-tight">Modulo inviato</div>
                  </div>
                  
                  <div className="bg-purple-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-purple-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-purple-700 mb-2">{candidatiStats.form_compilato}</div>
                    <div className="text-xs text-purple-600 font-medium leading-tight">Form compilato</div>
                  </div>
                  
                  <div className="bg-orange-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-orange-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-orange-700 mb-2">{candidatiStats.in_attesa_colloquio}</div>
                    <div className="text-xs text-orange-600 font-medium leading-tight">In attesa colloquio</div>
                  </div>
                  
                  <div className="bg-cyan-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-cyan-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-cyan-700 mb-2">{candidatiStats.colloquio_fissato}</div>
                    <div className="text-xs text-cyan-600 font-medium leading-tight">Colloquio fissato</div>
                  </div>
                  
                  <div className="bg-green-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-green-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-green-700 mb-2">{candidatiStats.inserito}</div>
                    <div className="text-xs text-green-600 font-medium leading-tight">Inserito</div>
                  </div>
                  
                  <div className="bg-red-100/80 backdrop-blur-xl rounded-2xl p-4 text-center border border-red-200 shadow-lg hover:shadow-xl transition-all duration-200">
                    <div className="text-3xl font-bold text-red-700 mb-2">{candidatiStats.scartato}</div>
                    <div className="text-xs text-red-600 font-medium leading-tight">Scartato</div>
                  </div>
                </div>
              </div>

              {/* Payment Stats */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Statistiche Pagamenti</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <Card className="bg-gray-100/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <DollarSign className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                      <div className="text-3xl font-bold text-gray-700 mb-2">{totalePagamenti}</div>
                      <div className="text-sm text-gray-600">Totale</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-3" />
                      <div className="text-3xl font-bold text-green-600 mb-2">{pagamentiStats.pagati}</div>
                      <div className="text-sm text-gray-600">Pagati</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                      <div className="text-3xl font-bold text-yellow-600 mb-2">{pagamentiStats.in_attesa}</div>
                      <div className="text-sm text-gray-600">In Attesa</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <XCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
                      <div className="text-3xl font-bold text-red-600 mb-2">{pagamentiStats.scaduti}</div>
                      <div className="text-sm text-gray-600">Scaduti</div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <BarChart3 className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                      <div className="text-3xl font-bold text-blue-600 mb-2">€{pagamentiStats.totale_incasso}</div>
                      <div className="text-sm text-gray-600">Incasso Totale</div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Attività Recenti</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  
                  {/* Recent Agencies */}
                  <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-blue-600" />
                        Agenzie Recenti
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {agenzie.slice(0, 5).map(agenzia => (
                          <div key={agenzia.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                            <div>
                              <div className="font-medium text-gray-900">{agenzia.nome_agenzia}</div>
                              <div className="text-sm text-gray-600">{agenzia.zona}</div>
                            </div>
                            <Badge className="bg-blue-100 text-blue-700 border-0">
                              {agenzia.posti_disponibili} posti
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recent Candidates */}
                  <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-green-600" />
                        Candidati Recenti
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {candidati.slice(0, 5).map(candidato => (
                          <div key={candidato.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                            <div>
                              <div className="font-medium text-gray-900">{candidato.nome} {candidato.cognome}</div>
                              <div className="text-sm text-gray-600">
                                {candidato.eta ? `${candidato.eta} anni` : 'Età non specificata'} • {candidato.citta || candidato.zona_interesse || 'Città non specificata'}
                              </div>
                            </div>
                            <Badge className={`border-0 ${candidato.stato_pipeline === 'Nuovo' ? 'bg-blue-100 text-blue-700' : 
                              candidato.stato_pipeline === 'Inserito' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                              {candidato.stato_pipeline}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Gestione Agenzie Tab */}
            <TabsContent value="agenzie" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
              <AdminPendingAgencies 
                onImpersonate={handleImpersonateUser}
                loadingImpersonation={loadingImpersonation}
              />
            </TabsContent>

            {/* Match Admin Tab */}
            <TabsContent value="match" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
              {(() => {
                console.log('[AdminPanel → MatchAdmin] Passing candidati:', candidati?.length || 0);
                console.log('[AdminPanel → MatchAdmin] Passing agenzie:', agenzie?.length || 0);
                return (
                  <MatchAdmin 
                    candidatiProp={candidati}
                    agenzieProp={agenzie}
                  />
                );
              })()}
            </TabsContent>

            {/* Notifiche Admin Tab */}
            <TabsContent value="notifiche" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
              <AdminNotifications />
            </TabsContent>

            {/* Old Agency Selection - Keep for reference or remove */}
            <TabsContent value="agenzie-old" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8" style={{display: 'none'}}>
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Gestione Agenzie</h2>
              </div>

              {/* Agency Selection */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-1 bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-blue-600" />
                      Seleziona Agenzia
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {agenzie.map(agenzia => (
                        <div 
                          key={agenzia.id}
                          onClick={() => setSelectedAgenzia(agenzia)}
                          className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                            selectedAgenzia?.id === agenzia.id 
                              ? 'bg-blue-50 border-blue-300 shadow-md' 
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1">{agenzia.nome_agenzia}</div>
                          <div className="text-sm text-gray-600 mb-2">{agenzia.zona}</div>
                          <div className="flex items-center justify-between">
                            <Badge className="bg-blue-100 text-blue-700 border-0">
                              {agenzia.posti_disponibili} posti
                            </Badge>
                            {selectedAgenzia?.id === agenzia.id && (
                              <Badge className="bg-green-100 text-green-700 border-0">
                                Selezionata
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Candidati Compatibili per Agenzie */}
                <div className="lg:col-span-2">
                  {selectedAgenzia ? (
                    <CompatibiliCandidati agenziaId={selectedAgenzia.id} />
                  ) : (
                    <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                      <CardContent className="p-8 text-center">
                        <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Seleziona un'agenzia</h3>
                        <p className="text-gray-600">
                          Scegli un'agenzia dalla lista per visualizzare i candidati compatibili.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Pagamenti Tab */}
            <TabsContent value="pagamenti" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Gestione Pagamenti</h2>
                <Button
                  onClick={() => setShowNuovoPagamentoModal(true)}
                  disabled={loading || !agenzieReady || !agenzie || agenzie.length === 0}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {loading || !agenzieReady || !agenzie || agenzie.length === 0 ? "Caricamento..." : "Nuovo Pagamento"}
                </Button>
              </div>

              {/* Payments Tabs */}
              <Tabs defaultValue="tutti" className="w-full">
                <TabsList className="bg-gray-100 rounded-2xl p-1">
                  <TabsTrigger value="tutti" className="rounded-xl">Tutti</TabsTrigger>
                  <TabsTrigger value="pagati" className="rounded-xl">Pagati</TabsTrigger>
                  <TabsTrigger value="attesa" className="rounded-xl">In Attesa</TabsTrigger>
                  <TabsTrigger value="scaduti" className="rounded-xl">Scaduti</TabsTrigger>
                </TabsList>

                <TabsContent value="tutti" className="mt-6">
                  <div className="grid gap-4">
                    {pagamenti.map(pagamento => (
                      <Card key={pagamento.id} className="bg-white/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="font-semibold text-gray-900">{pagamento.agenzia_nome}</h3>
                                <Badge className={getPagamentoStatusColor(pagamento.stato)}>
                                  {pagamento.stato}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                                <div>
                                  <span className="font-medium">Importo:</span> €{pagamento.importo}
                                </div>
                                <div>
                                  <span className="font-medium">Tipo:</span> {pagamento.tipo}
                                </div>
                                <div>
                                  <span className="font-medium">Scadenza:</span> {new Date(pagamento.scadenza).toLocaleDateString('it-IT')}
                                </div>
                                <div>
                                  <span className="font-medium">Data:</span> {new Date(pagamento.data_creazione).toLocaleDateString('it-IT')}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              {pagamento.stato === "In attesa" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => sendEmailSollecito(pagamento)}
                                  className="rounded-2xl border-orange-300 text-orange-600 hover:bg-orange-50"
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  Sollecito
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  // Open edit modal with this payment data
                                  setNuovoPagamento({
                                    agenzia_id: pagamento.agenzia_id,
                                    piano: pagamento.piano || "START",
                                    modalita_pagamento: pagamento.modalita_pagamento || "unico",
                                    data_attivazione: pagamento.data_attivazione || "",
                                    stato: pagamento.stato,
                                    note: pagamento.note || ""
                                  });
                                  setEditingPagamento(pagamento.id);
                                  setShowNuovoPagamentoModal(true);
                                }}
                                className="rounded-2xl border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="pagati">
                  <div className="grid gap-4">
                    {pagamenti.filter(p => p.stato === "Pagato").map(pagamento => (
                      <Card key={pagamento.id} className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">{pagamento.agenzia_nome}</h3>
                              <p className="text-sm text-gray-600">€{pagamento.importo} - {pagamento.tipo}</p>
                            </div>
                            <Badge className={getPagamentoStatusColor(pagamento.stato)}>
                              {pagamento.stato}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="attesa">
                  <div className="grid gap-4">
                    {pagamenti.filter(p => p.stato === "In attesa").map(pagamento => (
                      <Card key={pagamento.id} className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">{pagamento.agenzia_nome}</h3>
                              <p className="text-sm text-gray-600">€{pagamento.importo} - Scade: {new Date(pagamento.scadenza).toLocaleDateString('it-IT')}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => sendEmailSollecito(pagamento)}
                              className="bg-orange-500 hover:bg-orange-600 text-white rounded-2xl"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Sollecito
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="scaduti">
                  <div className="grid gap-4">
                    {pagamenti.filter(p => p.stato === "Scaduto").map(pagamento => (
                      <Card key={pagamento.id} className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2">{pagamento.agenzia_nome}</h3>
                              <p className="text-sm text-red-600">€{pagamento.importo} - Scaduto il: {new Date(pagamento.scadenza).toLocaleDateString('it-IT')}</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => sendEmailSollecito(pagamento)}
                              className="bg-red-500 hover:bg-red-600 text-white rounded-2xl"
                            >
                              <AlertTriangle className="w-4 h-4 mr-2" />
                              Urgente
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* Note Interne Tab */}
            <TabsContent value="note" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Note Interne</h2>
                <Button
                  onClick={() => setShowNuovaNotaModal(true)}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Nota
                </Button>
              </div>

              <div className="grid gap-4">
                {noteInterne.map(nota => (
                  <Card key={nota.id} className="bg-white/90 backdrop-blur-xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-semibold text-gray-900">{nota.titolo}</h3>
                            <Badge className={getNotaPriorityColor(nota.priorita)}>
                              {nota.priorita}
                            </Badge>
                            <Badge variant="outline" className="text-gray-600">
                              {nota.tipo_entita}
                            </Badge>
                          </div>
                          <p className="text-gray-700 mb-3 leading-relaxed">{nota.contenuto}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span><strong>Entità:</strong> {nota.entita_nome}</span>
                            <span><strong>Autore:</strong> {nota.autore}</span>
                            <span><strong>Data:</strong> {new Date(nota.data_creazione).toLocaleDateString('it-IT')}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              // Pre-populate form with existing note data
                              setNuovaNota({
                                tipo_entita: nota.tipo_entita,
                                entita_id: nota.entita_id,
                                testo: nota.contenuto || nota.titolo,
                                esito: nota.priorita === "alta" ? "da_richiamare" : "positivo"
                              });
                              setEditingNota(nota.id);
                              setShowNuovaNotaModal(true);
                            }}
                            className="rounded-2xl border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (window.confirm("Sei sicuro di voler eliminare questa nota?")) {
                                setNoteInterne(prev => prev.filter(n => n.id !== nota.id));
                                toast.success("Nota eliminata con successo");
                              }
                            }}
                            className="rounded-2xl border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Email Automation Tab */}
            <TabsContent value="email" className="space-y-6 sm:space-y-8 mt-6 sm:mt-8">
              <h2 className="text-2xl font-bold text-gray-900">Email Automation</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="w-5 h-5 text-blue-600" />
                      Follow-up Automatici
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <h3 className="font-semibold text-blue-900 mb-2">Solleciti Pagamenti</h3>
                        <p className="text-sm text-blue-700 mb-3">Invia automaticamente promemoria per pagamenti in scadenza</p>
                        <Button 
                          size="sm" 
                          onClick={() => toast.info("Configurazione Solleciti Pagamenti - Modalità Test attiva. Nessuna email reale verrà inviata.")}
                          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                        >
                          Configura
                        </Button>
                      </div>
                      
                      <div className="p-4 bg-green-50 rounded-xl">
                        <h3 className="font-semibold text-green-900 mb-2">Welcome Email</h3>
                        <p className="text-sm text-green-700 mb-3">Email di benvenuto per nuove agenzie</p>
                        <Button 
                          size="sm" 
                          onClick={() => toast.info("Configurazione Welcome Email - Modalità Test attiva. Nessuna email reale verrà inviata.")}
                          className="bg-green-600 hover:bg-green-700 text-white rounded-xl"
                        >
                          Configura
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/90 backdrop-blur-xl border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-green-600" />
                      Statistiche Email
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Email inviate oggi</span>
                        <span className="font-bold text-gray-900">24</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Tasso apertura</span>
                        <span className="font-bold text-green-600">68%</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                        <span className="text-gray-700">Email in coda</span>
                        <span className="font-bold text-blue-600">12</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Candidati Tab */}
            <TabsContent value="candidati" className="space-y-6 mt-6 sm:mt-8">
              <AdminCandidatiList 
                candidati={adminUsersList.candidati}
                onImpersonate={handleImpersonateUser}
                loadingImpersonation={loadingImpersonation}
              />
            </TabsContent>

            {/* Agenzie Tab - Now using AdminPendingAgencies component defined above */}

            {/* Assistenza Tab */}
            <TabsContent value="assistenza" className="space-y-6 mt-6 sm:mt-8">
              <AdminSupporto />
            </TabsContent>

            {/* Formazione Tab */}
            <TabsContent value="formazione" className="space-y-6 mt-6 sm:mt-8">
              <AdminFormazione />
            </TabsContent>

            {/* Impostazioni Tab */}
            <TabsContent value="impostazioni" className="space-y-6 mt-6 sm:mt-8">
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Settings className="w-6 h-6 text-blue-600" />
                    Impostazioni Generali
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-blue-50 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Sistema di Impersonation</h3>
                      <p className="text-gray-600 mb-4">
                        Gestisci l'accesso alle dashboard degli utenti in modalità amministratore.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="bg-white rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Sicurezza</h4>
                          <ul className="text-gray-600 space-y-1">
                            <li>• Modalità solo lettura</li>
                            <li>• Sessioni con scadenza (2 ore)</li>
                            <li>• Log delle attività admin</li>
                          </ul>
                        </div>
                        <div className="bg-white rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Funzionalità</h4>
                          <ul className="text-gray-600 space-y-1">
                            <li>• Accesso diretto alle dashboard</li>
                            <li>• Nessun logout necessario</li>
                            <li>• Ritorno rapido al pannello admin</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    {/* Notifiche Admin */}
                    <div className="bg-blue-50 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">🔔 Sistema Notifiche</h3>
                      <p className="text-gray-600 mb-4">
                        Gestisci le notifiche admin e le comunicazioni del sistema.
                      </p>
                      <div className="flex items-center justify-between bg-white rounded-xl p-4 mb-4">
                        <div className="flex items-center gap-3">
                          <Bell className="w-6 h-6 text-blue-600" />
                          <div>
                            <div className="font-semibold text-gray-900">Notifiche Non Lette</div>
                            <div className="text-sm text-gray-600">Registrazioni in attesa, comunicazioni sistema</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {unreadNotifications > 0 && (
                            <Badge className="bg-red-500 text-white px-3 py-1 text-sm">
                              {unreadNotifications > 99 ? '99+' : unreadNotifications}
                            </Badge>
                          )}
                          <Button
                            onClick={() => setActiveTab('notifiche')}
                            variant="outline"
                            size="sm"
                            className="rounded-xl border-blue-300 text-blue-600 hover:bg-blue-50"
                          >
                            Gestisci Notifiche
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Gestione Pagamenti */}
                    <div className="bg-green-50 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">💳 Gestione Pagamenti</h3>
                      <p className="text-gray-600 mb-4">
                        Monitora e gestisci i pagamenti delle agenzie partner.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <Card className="bg-white border-0">
                          <CardContent className="p-4 text-center">
                            <DollarSign className="w-6 h-6 text-green-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-green-600">{pagamentiStats.pagati}</div>
                            <div className="text-sm text-gray-600">Pagati</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-white border-0">
                          <CardContent className="p-4 text-center">
                            <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-yellow-600">{pagamentiStats.in_attesa}</div>
                            <div className="text-sm text-gray-600">In Attesa</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-white border-0">
                          <CardContent className="p-4 text-center">
                            <XCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-red-600">{pagamentiStats.scaduti}</div>
                            <div className="text-sm text-gray-600">Scaduti</div>
                          </CardContent>
                        </Card>
                        <Card className="bg-white border-0">
                          <CardContent className="p-4 text-center">
                            <BarChart3 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-blue-600">€{pagamentiStats.totale_incasso}</div>
                            <div className="text-sm text-gray-600">Incasso Totale</div>
                          </CardContent>
                        </Card>
                      </div>
                      <Button
                        onClick={() => setActiveTab('pagamenti')}
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Gestisci Pagamenti Dettagliati
                      </Button>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">📊 Informazioni Sistema</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Candidati totali:</span>
                          <span className="font-semibold ml-2">{adminUsersList.total_candidati || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Agenzie totali:</span>
                          <span className="font-semibold ml-2">{adminUsersList.total_agenzie || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Sessioni attive:</span>
                          <span className="font-semibold ml-2">{impersonationSession ? 1 : 0}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Nuovo: Gestione Utenti e Backup */}
                    <div className="bg-green-50 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">🔧 Strumenti Admin</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900 text-sm">Gestione Database</h4>
                          <div className="space-y-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="w-full justify-start rounded-xl border-green-300 text-green-700 hover:bg-green-100"
                              onClick={() => window.open('/api/candidati', '_blank')}
                            >
                              📊 Esporta Candidati
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="w-full justify-start rounded-xl border-green-300 text-green-700 hover:bg-green-100"
                              onClick={() => window.open('/api/agenzie', '_blank')}
                            >
                              🏢 Esporta Agenzie  
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="w-full justify-start rounded-xl border-green-300 text-green-700 hover:bg-green-100"
                              onClick={() => window.open('/api/richieste-colloquio', '_blank')}
                            >
                              📅 Esporta Richieste Colloquio
                            </Button>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <h4 className="font-semibold text-gray-900 text-sm">Monitoraggio</h4>
                          <div className="bg-white rounded-xl p-4 border border-green-200">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Sistema:</span>
                                <span className="text-green-600 font-semibold">🟢 Online</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Database:</span>
                                <span className="text-green-600 font-semibold">🟢 Connesso</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">API:</span>
                                <span className="text-green-600 font-semibold">🟢 Operativo</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Ultimo Backup:</span>
                                <span className="text-gray-900 font-semibold">{new Date().toLocaleDateString('it-IT')}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Nuovo: Quick Actions */}
                    <div className="bg-purple-50 rounded-2xl p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">⚡ Azioni Rapide</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Button 
                          variant="outline"
                          size="sm" 
                          className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-100"
                          onClick={() => setActiveTab("candidati")}
                        >
                          👤 Candidati
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-100"
                          onClick={() => setActiveTab("agenzie")}
                        >
                          🏢 Agenzie
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-100"
                          onClick={() => setActiveTab("pagamenti")}
                        >
                          💳 Pagamenti
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          className="rounded-xl border-purple-300 text-purple-700 hover:bg-purple-100"
                          onClick={() => setActiveTab("overview")}
                        >
                          📊 Dashboard
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Nuovo Pagamento Modal */}
        {showNuovoPagamentoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{overflow: 'hidden'}}>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col" style={{minHeight: '400px'}}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingPagamento ? "Modifica Pagamento" : "Nuovo Pagamento"}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => closePagamentoModal()}
                  className="rounded-full"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <form onSubmit={handleNuovoPagamento} className="space-y-4">
                <div>
                  <Label>Agenzia</Label>
                  {agenzieReady && agenzie && agenzie.length > 0 ? (
                    <Select 
                      value={nuovoPagamento.agenzia_id} 
                      onValueChange={(value) => setNuovoPagamento({...nuovoPagamento, agenzia_id: value})}
                    >
                      <SelectTrigger className="rounded-2xl">
                        <SelectValue placeholder="Seleziona agenzia" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px] overflow-y-auto">
                        {agenzie.map((agenzia) => (
                          <SelectItem 
                            key={agenzia.id} 
                            value={agenzia.id}
                          >
                            {agenzia.nome_agenzia}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="h-12 px-3 py-2 border border-gray-200 rounded-2xl bg-gray-50 flex items-center text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Caricamento agenzie...
                    </div>
                  )}
                </div>

                <div>
                  <Label>Piano Abbonamento</Label>
                  <Select value={nuovoPagamento.piano} onValueChange={(value) => setNuovoPagamento({...nuovoPagamento, piano: value})}>
                    <SelectTrigger className="rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="START">START - €750 (1 mese)</SelectItem>
                      <SelectItem value="GROW">GROW - €3.500 (6 mesi)</SelectItem>
                      <SelectItem value="ELITE">ELITE - €5.000 (10 mesi)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Modalità di Pagamento</Label>
                  <Select value={nuovoPagamento.modalita_pagamento} onValueChange={(value) => setNuovoPagamento({...nuovoPagamento, modalita_pagamento: value})}>
                    <SelectTrigger className="rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unico">Pagamento Unico</SelectItem>
                      <SelectItem value="3_rate">3 Rate</SelectItem>
                      <SelectItem value="4_rate">4 Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Data Attivazione</Label>
                  <Input
                    type="date"
                    value={nuovoPagamento.data_attivazione}
                    onChange={(e) => setNuovoPagamento({...nuovoPagamento, data_attivazione: e.target.value})}
                    className="rounded-2xl"
                  />
                </div>

                {editingPagamento && (
                  <div>
                    <Label>Stato Pagamento</Label>
                    <Select value={nuovoPagamento.stato} onValueChange={(value) => setNuovoPagamento({...nuovoPagamento, stato: value})}>
                      <SelectTrigger className="rounded-2xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pagato">Pagato</SelectItem>
                        <SelectItem value="in_attesa">In Attesa</SelectItem>
                        <SelectItem value="scaduto">Scaduto</SelectItem>
                        <SelectItem value="annullato">Annullato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>Note</Label>
                  <Textarea
                    value={nuovoPagamento.note}
                    onChange={(e) => setNuovoPagamento({...nuovoPagamento, note: e.target.value})}
                    className="rounded-2xl"
                    placeholder="Note aggiuntive..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => closePagamentoModal()}
                    className="rounded-2xl"
                  >
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    className="rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                    disabled={loading}
                  >
                    {loading ? "Salvando..." : "Salva Pagamento"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Nuova Nota Modal */}
        {showNuovaNotaModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{overflow: 'hidden'}}>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col" style={{minHeight: '400px'}}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingNota ? "Modifica Nota Interna" : "Nuova Nota Interna"}
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowNuovaNotaModal(false)}
                  className="rounded-full"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <form onSubmit={handleNuovaNota} className="space-y-4">
                <div>
                  <Label>Tipo Entità</Label>
                  <Select value={nuovaNota.tipo_entita} onValueChange={(value) => setNuovaNota({...nuovaNota, tipo_entita: value, entita_id: ""})}>
                    <SelectTrigger className="rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agenzia">Agenzia</SelectItem>
                      <SelectItem value="candidato">Candidato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>{nuovaNota.tipo_entita === "agenzia" ? "Agenzia" : "Candidato"}</Label>
                  <Select value={nuovaNota.entita_id} onValueChange={(value) => setNuovaNota({...nuovaNota, entita_id: value})}>
                    <SelectTrigger className="rounded-2xl">
                      <SelectValue placeholder={`Seleziona ${nuovaNota.tipo_entita}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {nuovaNota.tipo_entita === "agenzia" 
                        ? agenzie.map(agenzia => (
                            <SelectItem key={agenzia.id} value={agenzia.id}>
                              {agenzia.nome_agenzia}
                            </SelectItem>
                          ))
                        : candidati.map(candidato => (
                            <SelectItem key={candidato.id} value={candidato.id}>
                              {candidato.nome} {candidato.cognome}
                            </SelectItem>
                          ))
                      }
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Testo Nota</Label>
                  <Textarea
                    value={nuovaNota.testo}
                    onChange={(e) => setNuovaNota({...nuovaNota, testo: e.target.value})}
                    className="rounded-2xl resize-none"
                    rows={4}
                    placeholder="Inserisci il testo della nota..."
                    required
                  />
                </div>

                <div>
                  <Label>Esito</Label>
                  <Select value={nuovaNota.esito} onValueChange={(value) => setNuovaNota({...nuovaNota, esito: value})}>
                    <SelectTrigger className="rounded-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="positivo">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Positivo
                        </div>
                      </SelectItem>
                      <SelectItem value="negativo">
                        <div className="flex items-center gap-2">
                          <XCircle className="w-4 h-4 text-red-500" />
                          Negativo
                        </div>
                      </SelectItem>
                      <SelectItem value="da_richiamare">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-yellow-500" />
                          Da Richiamare
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowNuovaNotaModal(false)}
                    className="flex-1 rounded-2xl"
                  >
                    Annulla
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 rounded-2xl bg-green-600 hover:bg-green-700 text-white"
                  >
                    Salva
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile Bottom Navigation */}
      {/* Mobile Bottom Navigation - Hidden on Desktop */}
      <div className="block md:hidden">
        <MobileBottomNav />
      </div>
    </div>
  );
};

export default AdminPanel;